import { Delivery, createDelivery, deleteDelivery } from '../../../../model/delivery';
import { IncomingItemGroup, Order, OutgoingItemGroup } from '../../../../model/order';
import { Invoice, createInvoice, deleteInvoice } from '../../../../model/invoice';
import React, { useEffect, useState } from 'react';

import { Address } from '../../../../model/company';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';
import { FormSelect } from '../../../../components/Form/Select/FormSelect';
import { PercentageField } from '../../../../components/Form/TextField/PercentageField';
import { Stack } from '../../../../components/Stack/Stack';
import { applyTax } from '../../../../model/price';
import { cacheRequest } from '../../../../model/cache';
import { editItems } from '../../../../model/item';
import { getProductTaxDetails } from '../../../../model/product';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props {
  order: Order;
  itemIds: string[];
  preferedAddresses: Address;
  document: Invoice | Delivery | undefined;
  documentType?: 'invoice' | 'delivery';
  item?: OutgoingItemGroup | IncomingItemGroup;
  onClose: () => void;
}

export function EditDocumentForm({
  order,
  itemIds,
  preferedAddresses,
  document,
  documentType,
  item,
  onClose,
}: Props): JSX.Element {
  const itemGroup = order.itemGroups?.filter(
    (group) => group.__typename === 'OutgoingItemGroup' && group.invoiceId === document?.id
  )[0];

  const existingVatRate = (itemGroup as OutgoingItemGroup)?.vatPercentage;

  const countryCode = order.deliveries[0]?.address.countryCode?.toLowerCase() ?? 'de';

  const [productTaxRate, setProductTaxRate] = useState<number>();
  const productId = item?.productId;
  const productVariantId = item?.productVariantId;

  useEffect(() => {
    if (productId && productVariantId) {
      const fetchProductDetails = async (): Promise<void> => {
        const vat = await cacheRequest(
          `product_${productId}_${productVariantId}_${countryCode}_tax`,
          () =>
            getProductTaxDetails({
              productId,
              countryCode,
              productVariantId,
            }),
          1000 * 60 * 10 // 10 mins, we want to de-dup not cache.
        );
        setProductTaxRate(vat);
      };
      fetchProductDetails();
    }
  }, [countryCode, productId, productVariantId]);

  let options: Array<Invoice | Delivery>;

  const isInvoice = documentType === 'invoice';

  if (!isInvoice) {
    options = order.deliveries.filter((del) => del.status === 'draft' || del.status === 'pending');
  } else {
    options = order.invoices.filter((inv) => inv.status === 'draft' || inv.status === 'pending');
  }

  const { model, fields, valid, dirty, onSubmit, submissionStatus } = useForm<{
    id?: string;
    price?: number;
    vatPercentage?: number;
  }>({
    model: {
      id: document?.id,
      price: item?.price || 0,
    },
    validations: {
      id: 'required',
    },
    onSubmit: async () => {
      let createDocument, deleteDocument;
      let editPropertyName = '';
      let document: Invoice | Delivery | null = null;

      if (isInvoice) {
        createDocument = createInvoice;
        deleteDocument = deleteInvoice;
        editPropertyName = 'invoiceId';
      } else {
        createDocument = createDelivery;
        deleteDocument = deleteDelivery;
        editPropertyName = 'deliveryId';
      }

      try {
        if (model.id === 'new') {
          document = await createDocument(order.id, preferedAddresses);

          await editItems(order.id, itemIds, {
            [editPropertyName]: document?.id,
            ...(isInvoice
              ? {
                  price: Number(model.price),
                  vatPercentage: model.vatPercentage,
                }
              : {}),
          });
        } else if (model.id === 'no') {
          await editItems(order.id, itemIds, {
            [editPropertyName]: null,
          });
        } else if (model.id) {
          await editItems(order.id, itemIds, {
            [editPropertyName]: model.id,
            ...(isInvoice
              ? {
                  price: Number(model.price),
                  vatPercentage: model.vatPercentage,
                }
              : {}),
          });
        }
        onClose();
      } catch (e) {
        if (model.id === 'new' && document) {
          await deleteDocument(order.id, document.id);
        }
      }
    },
    onSubmitError: (error) => handleError(error),
  });

  const vatRate = existingVatRate || productTaxRate;

  useEffect(() => {
    if (vatRate !== undefined && model.vatPercentage === undefined) {
      fields.vatPercentage.onChange(vatRate);
    }
  }, [vatRate]);

  return (
    <FormForm
      onSubmit={onSubmit}
      onClose={onClose}
      canSubmit={dirty && valid}
      loading={submissionStatus === 'submitting'}
    >
      <Stack>
        <FormSelect
          {...fields.id}
          label={documentType ? t.model[documentType]._name : ''}
          options={[
            { value: '', label: '' },
            {
              value: 'new',
              label: `${documentType && t.model[documentType].new}`,
            },
            ...options.map((inv) => ({
              value: inv.id,
              label: inv.reference,
            })),
          ]}
        />
        <Collapse open={isInvoice && model.id !== undefined && model.id !== 'no'}>
          <Stack>
            <FormNumberField {...fields.price} label={t.model.price.netPrice} />
            <PercentageField {...fields.vatPercentage} label={t.model.item.vatPercentage} />
            <FormNumberField
              value={applyTax(model.price || 0, model.vatPercentage || 0)}
              disabled
              label={t.model.price.grossPrice}
            />
          </Stack>
        </Collapse>
      </Stack>
    </FormForm>
  );
}
